import LinearProgress from '@material-ui/core/LinearProgress';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValueLoadable } from 'recoil';
import { ProductPopulates, ProductRecord } from '../../../@sprinx/knihovka-types';
import { productParameterGroupTypesQuery } from '../../../api/products/productParameterGroupTypes';
import { productParameterTypesQuery } from '../../../api/products/productParameterTypes';
import { productDetailGlobalParameters } from '../../../config';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import ParameterGroup from './ParameterGroup';
import Parameter from './Parameter';

export interface ProductDetailSpagettyParametersProps
  extends StandardDivProps<ProductDetailSpagettyParametersClassKey> {
  anchor?: string;
  onNaviButtonShow?: (state?: boolean) => void;
  product: { ean: string; sku: string } & ProductPopulates['parameters'];
}

export type ProductDetailSpagettyParametersClassKey = 'root' | 'headline';

const themeSettings = { name: 'ProductDetailSpagettyParameters' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<ProductDetailSpagettyParametersClassKey, {}>({
      root: {},
      headline: {
        ...theme.typography.h4,
      },
    }),
  themeSettings,
);

const ProductDetailSpagettyParameters = React.forwardRef<HTMLDivElement, ProductDetailSpagettyParametersProps>(
  ({ anchor, className, classes: pClasses, onNaviButtonShow, product }, ref) => {
    const classes = useStyles({ classes: pClasses });
    React.useEffect(() => {
      if (onNaviButtonShow) onNaviButtonShow(!!product.parameters);
    }, [onNaviButtonShow, product.parameters]);
    const t = useTranslateWithFallback();

    return (
      <div ref={ref} className={clsx(classes.root, className)} id={anchor ? anchor.replace('#', '') : undefined}>
        {productDetailGlobalParameters && (
          <>
            <h2 className={classes.headline}>{t('productPage/parameters')}</h2>
            <GlobalParameters product={product as ProductRecord} parameters={productDetailGlobalParameters} />
          </>
        )}
        {product.parameters && <ParameterGroups parameters={product.parameters} />}
      </div>
    );
  },
);

ProductDetailSpagettyParameters.displayName = 'ProductDetailSpagettyParameters';

export default withThemeProps(themeSettings)(ProductDetailSpagettyParameters);

const GlobalParameters = ({ parameters, product }: { parameters: string[]; product: ProductRecord }): JSX.Element => {
  const t = useTranslateWithFallback();

  return (
    <>
      {product.extra &&
        parameters.map((parameterName) => (
          <Parameter
            key={parameterName}
            label={t(`common/${parameterName}`)}
            value={product.extra ? product.extra[parameterName] : t('common/parameterNotFound')}
          />
        ))}
    </>
  );
};

const ParameterGroups = ({
  parameters,
}: {
  parameters: Required<ProductPopulates['parameters']>['parameters'];
}): JSX.Element | null => {
  const t = useTranslateWithFallback();

  const groupsLoadable = useRecoilValueLoadable(productParameterGroupTypesQuery);
  const typesLoadable = useRecoilValueLoadable(productParameterTypesQuery);

  if (groupsLoadable.state === 'loading') return <LinearProgress />;
  if (typesLoadable.state === 'loading') return <LinearProgress />;
  if (groupsLoadable.state === 'hasError') {
    console.error('Parameter Groups loading error', groupsLoadable.contents);
    return null;
  }
  if (typesLoadable.state === 'hasError') {
    console.error('Parameter Types loading error', typesLoadable.contents);
    return null;
  }

  return (
    <>
      {groupsLoadable.contents.map((group) => (
        <ParameterGroup
          key={group.code}
          label={t(group.label, { acceptString: true })}
          types={typesLoadable.contents}
          parameters={parameters.filter((p) => p.group === group.code)}
        />
      ))}
    </>
  );
};
